import { useState, Suspense, Children, ReactNode } from "react";
import styled, { keyframes, css } from "styled-components/macro";
import { Box } from "./Box";
import Portal from "./Portal";
import useStackContext, {
  StackContextProvider,
} from "../hooks/use-stack-context";

export default function StackingBox({
  children,
  height,
}: {
  children: ReactNode;
  height?: number;
}) {
  const arrayChildren = Children.toArray(children);
  const numChildren = arrayChildren.length;

  return (
    <Container>
      {arrayChildren.map((child, index) => {
        const offset = numChildren - (index + 1);
        const scale = ((index + 1) / numChildren + 9) / 10;
        const opacity = index + 2 >= numChildren ? 1 : 0;
        return (
          <Child
            key={index}
            first={index === 0}
            style={{
              transform: `scale(${scale}) translateY(-${10 * offset}px)`,
              opacity,
              height: index === 0 ? undefined : height,
            }}
          >
            {child}
          </Child>
        );
      })}
    </Container>
  );
}

export function StackingBoxModal({
  children,
  transparent = false,
  height = 760,
}: {
  children: ReactNode;
  transparent?: boolean;
  height?: number;
}) {
  const zIndex = useStackContext();
  return (
    <StackContextProvider>
      <Portal>
        <Wrapper
          style={{
            zIndex,
            ...(transparent ? { background: "transparent" } : {}),
          }}
        >
          <OverlayContainer>
            <Suspense fallback={<Box />}>
              <StackingBox height={height}>{children}</StackingBox>
            </Suspense>
          </OverlayContainer>
        </Wrapper>
      </Portal>
    </StackContextProvider>
  );
}

export type StackingBoxTrigger = (props: { show: () => void }) => ReactNode;
export type StackingBoxContent = (props: {
  hide: () => void;
}) => { props: { children: ReactNode } };

/**
 * same as <Overlay/> but renders <Box/> like components stacked
 * content renderer should use a fragment as first child
 * <>
 *   <Box>...</Box>
 *   <Box>...</Box>
 * </>
 */
export function StackingBoxOverlay({
  trigger,
  content,
}: {
  trigger: StackingBoxTrigger;
  content: StackingBoxContent;
}) {
  const [isShowing, setIsShowing] = useState(false);
  return (
    <>
      {trigger({ show: () => setIsShowing(true) })}
      {isShowing && (
        <StackingBoxModal>
          {content({ hide: () => setIsShowing(false) }).props.children || []}
        </StackingBoxModal>
      )}
    </>
  );
}

const Container = styled.div`
  position: relative;
  height: 100%;
  flex: 1;
`;

const fadeIn = keyframes`
  from{
    transform: translateY(100px);
    opacity: 0;
  }
  to{
    transform: translateY(0px);
    opacity: 1;
  }
`;

const Child = styled.div<{ first?: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition-duration: 0.2s;
  transform-origin: top center;
  & > ${Box} {
    max-height: 100%;
    ${(p) =>
      !p.first &&
      css`
        height: 100%;
      `}
  }
  animation: 0.4s ${fadeIn};
`;

const Wrapper = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  height: 100%;
  overflow: auto;
  z-index: 10000;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #0005;
`;

const OverlayContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: 40px;
  width: 60vw;
  max-height: 100%;
  max-width: 600px;
  height: 760px;
  @media (max-width: 1000px) {
    width: 100%;
    max-width: unset;
    padding: 8px;
    height: calc(100% - 16px);
  }
  @media (max-height: 700px) {
    min-height: 100%;
  }
`;
